<template>
    <div>
        <b-alert variant="danger" show v-if="error">
            {{ error }}
        </b-alert>

        <div class="d-flex justify-content-between align-items-center">
            <div>
                {{ $t("Card Number") }}
            </div>
            <div>
                <b-btn
                    v-if="onboardingUser.authorizeProfile"
                    @click="cancelCreateCard"
                    variant="link"
                >
                    {{ $t("Cancel") }}
                </b-btn>
            </div>
        </div>

        <b-form-input
            v-model="card"
            autocomplete="cc-number"
            inputmode="numeric"
            class="input-element"
            :placeholder="$t('Card Number')"
            size="lg"
        />

        <b-row no-gutters class="mt-2">
            <b-col cols="3">
                {{ $t("Expiration") }}
            </b-col>
            <b-col cols="3"> </b-col>
            <b-col cols="4" offset="2">
                {{ $t("Security Code") }}
            </b-col>
        </b-row>

        <b-row no-gutters>
            <b-col cols="3">
                <b-form-select
                    v-model="month"
                    :options="expirationMonths"
                    autocomplete="cc-exp-month"
                    size="lg"
                >
                    <template #first>
                        <b-form-select-option :value="null" disabled>
                            <span v-if="$screen.md">
                                {{ $t("Month") }}
                            </span>
                            <span v-else>
                                {{ $t("MM") }}
                            </span>
                        </b-form-select-option>
                    </template>
                </b-form-select>
            </b-col>
            <b-col cols="3">
                <b-form-select
                    v-model="year"
                    :options="expirationYears"
                    autocomplete="cc-exp-year"
                    size="lg"
                >
                    <template #first>
                        <b-form-select-option :value="null" disabled>
                            <span v-if="$screen.md">
                                {{ $t("Year") }}
                            </span>
                            <span v-else>
                                {{ $t("YY") }}
                            </span>
                        </b-form-select-option>
                    </template>
                </b-form-select>
            </b-col>
            <b-col cols="4" offset="2">
                <b-form-input
                    type="text"
                    v-model="code"
                    autocomplete="cc-csc"
                    size="lg"
                    :placeholder="$t('CVV')"
                ></b-form-input>
            </b-col>
        </b-row>

        <div class="mt-2">
            {{ $t("Country") }}
        </div>
        <b-select
            size="lg"
            v-model="countryId"
            :options="countriesMapped"
            autocomplete="off"
        >
        </b-select>

        <div v-if="!skipPostcode">
            <div class="mt-2">
                {{ zipOrPostalCode }}
            </div>
            <b-form-input
                type="text"
                v-model="postalCode"
                autocomplete="postal-code"
                size="lg"
                :placeholder="zipOrPostalCode"
            ></b-form-input>
        </div>

        <div class="mt-3 d-flex align-items-center justify-content-between">
            <b-form-checkbox v-model="save" switch size="lg" class="mr-2">
                {{ $t("Save card") }}
                <span v-if="$screen.md">
                    {{ $t("for future purchases") }}
                </span>
            </b-form-checkbox>
            <b-btn variant="light" pill @click="saveCardModal = true">
                <font-awesome-icon
                    class="text-success"
                    icon="fa-duotone fa-shield-check"
                />
            </b-btn>
        </div>

        <b-modal
            :title="$t('Payment Security')"
            v-model="saveCardModal"
            hide-footer
            centered
        >
            <h5 class="text-success">
                <font-awesome-icon
                    icon="fa-duotone fa-shield-check"
                    size="lg"
                />

                {{ $t("Your card details are secure") }}
            </h5>
            <div class="mt-2">
                {{
                    $t(
                        "Our payment processor will provide us with a unique ID that represents your card, enabling our system to charge your card upon your request"
                    )
                }}
            </div>
            <div class="mt-2">
                {{
                    $t(
                        "Your full card number, expiration date, and security code are never stored on our systems"
                    )
                }}
            </div>
        </b-modal>

        <!-- Retry countdown UI -->
        <transition name="fade">
            <div v-if="countdown > 0" class="mt-3">
                <div
                    class="d-flex justify-content-between align-items-center mb-1"
                >
                    <div class="small">
                        {{ $t("Please wait") }}
                        {{ countdown }}
                        {{ $t("seconds before retrying...") }}
                    </div>
                </div>

                <b-progress
                    :value="progressValue"
                    :max="throttleTotalSeconds || 1"
                    animated
                    height="0.75rem"
                />
                <div class="mt-1 small text-opaque">
                    {{
                        $t(
                            "This brief pause helps prevent accidental double-charges."
                        )
                    }}
                </div>
            </div>
        </transition>

        <b-btn
            :disabled="disabled"
            @click="createPaymentMethodAndCharge"
            size="lg"
            variant="primary"
            pill
            block
            class="mt-3"
        >
            {{ $t("Pay") }} ${{ price.amount }}
            <div class="small text-opaque">
                {{ price.localPrice }}
            </div>
        </b-btn>
    </div>
</template>

<script>
import { mapState } from "vuex";

export default {
    data() {
        return {
            saveCardModal: false,
            save: false,
            countryId: null,
            card: null,
            month: null,
            year: null,
            code: null,
            postalCode: null,
            countdown: null,
            countdownInterval: null,
            throttleTotalSeconds: 0, // original throttle length for progress bar
            unsubscribeToMutations: null,
        };
    },
    created() {
        if (this.onboardingUser.countryId) {
            this.countryId = this.onboardingUser.countryId;
        }

        this.unsubscribeToMutations = this.$store.subscribe((mutation) => {
            if (mutation.type === "authorize/setThrottle" && mutation.payload) {
                // Clear any previous timer before starting a new one
                if (this.countdownInterval) {
                    clearInterval(this.countdownInterval);
                    this.countdownInterval = null;
                }

                const payloadSeconds = Number(mutation.payload) || 0;
                this.countdown = payloadSeconds;
                this.throttleTotalSeconds = payloadSeconds;

                if (this.countdown > 0) {
                    this.countdownInterval = setInterval(() => {
                        if (this.countdown > 0) {
                            this.countdown = this.countdown - 1;
                        } else {
                            clearInterval(this.countdownInterval);
                            this.countdownInterval = null;
                        }
                    }, 1000);
                }
            }
        });
    },
    beforeDestroy() {
        if (this.unsubscribeToMutations) {
            this.unsubscribeToMutations();
        }
        if (this.countdownInterval) {
            clearInterval(this.countdownInterval);
            this.countdownInterval = null;
        }
    },
    computed: {
        selectedCountry() {
            return this.countries.find(
                (country) => country.id === this.countryId
            );
        },
        skipPostcode() {
            return this.selectedCountry?.doesntUsePostcode;
        },
        zipOrPostalCode() {
            return this.selectedCountry?.zip ? "ZIP Code" : "Postal Code";
        },
        countriesMapped() {
            return this.countries.map((country) => ({
                value: country.id,
                text: country.name,
            }));
        },
        ...mapState("options", [
            "expirationYears",
            "expirationMonths",
            "countries",
        ]),
        ...mapState("wallet", ["price"]),
        ...mapState("brand", ["brand"]),
        ...mapState("authorize", ["saving", "error"]),
        expiration() {
            return this.year && this.month;
        },
        complete() {
            return (
                this.card &&
                this.expiration &&
                this.code &&
                (this.postalCode || this.skipPostcode)
            );
        },
        disabled() {
            return this.saving || !this.complete || this.countdown > 0;
        },
        // Progress bar value increases from 0 -> throttleTotalSeconds
        progressValue() {
            const totalSeconds = this.throttleTotalSeconds || 0;
            const remainingSeconds = Math.max(this.countdown || 0, 0);
            return totalSeconds > 0 ? totalSeconds - remainingSeconds : 0;
        },
        ...mapState({
            onboardingUser: (state) => state.onboarding.user,
        }),
    },
    methods: {
        cancelCreateCard() {
            this.$store.commit("wallet/setCreateCard", false);
        },
        changePackage() {
            this.$store.commit("wallet/setPrice", null);
        },
        createPaymentMethodAndCharge() {
            this.$store.dispatch("authorize/transaction", {
                priceId: this.price.id,
                card: this.card,
                year: this.year,
                month: this.month,
                code: this.code,
                postalCode: this.postalCode,
                countryId: this.countryId,
                save: this.save,
            });
        },
    },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}
.fade-enter,
.fade-leave-to {
    opacity: 0;
}
.text-opaque {
    opacity: 0.7;
}
</style>
